import { getApps, initializeApp } from "firebase/app";
import {
  getAuth,
  getIdTokenResult,
  getMultiFactorResolver,
  GoogleAuthProvider,
  multiFactor,
  setPersistence,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut as firebaseSignOut,
  TotpMultiFactorGenerator,
  type Auth,
  type MultiFactorError,
  type MultiFactorResolver,
  type TotpSecret,
  type User,
  type UserCredential,
} from "firebase/auth";
import type {
  AuthGateway,
  AuthenticatedSession,
  PrimarySignInResult,
  RestoredAuthState,
} from "./auth-types";
import { SafeAuthError } from "./auth-types";
import { CENTRAL_FIREBASE_CONFIG } from "./firebase-config";
import { toSafeFirebaseAuthError } from "./firebase-auth-error";
import {
  authenticatedTokenUser,
  type CachedFirebaseAccessToken,
  usableCachedAccessToken,
} from "./firebase-token";
import { TOTP_ISSUER } from "./totp-qr";
import { validMfaCode } from "./mfa-code";
import { selectFirebaseAuthPersistence } from "./firebase-auth-persistence";

const SESSION_LIMIT_MILLISECONDS = 8 * 60 * 60 * 1000;

interface FirebaseAuthFailure {
  readonly code?: string;
}

interface VerifiedFirebaseSession {
  readonly session: AuthenticatedSession;
  readonly accessToken: CachedFirebaseAccessToken;
}

async function secureSession(
  user: User,
  forceRefresh = false,
): Promise<VerifiedFirebaseSession> {
  if (!user.emailVerified) throw new SafeAuthError("access-denied");
  const token = await getIdTokenResult(user, forceRefresh);
  const authenticatedAt = Date.parse(token.authTime);
  const expiresAtMs = Date.parse(token.expirationTime);
  if (
    !Number.isFinite(authenticatedAt) ||
    !Number.isFinite(expiresAtMs) ||
    Date.now() - authenticatedAt > SESSION_LIMIT_MILLISECONDS
  ) {
    throw new SafeAuthError("session-expired");
  }
  if (token.signInSecondFactor !== TotpMultiFactorGenerator.FACTOR_ID) {
    throw new SafeAuthError("access-denied");
  }
  const provider = token.signInProvider;
  if (provider !== "google.com" && provider !== "password")
    throw new SafeAuthError("access-denied");
  let response: Response;
  try {
    response = await fetch(
      `${import.meta.env.VITE_API_BASE_URL ?? ""}/v1/me/access`,
      {
        headers: {
          Authorization: `Bearer ${token.token}`,
          Accept: "application/json",
        },
        cache: "no-store",
        signal: AbortSignal.timeout(15_000),
      },
    );
  } catch {
    throw new SafeAuthError("network-unavailable");
  }
  if (!response.ok)
    throw new SafeAuthError(
      response.status >= 500 ? "network-unavailable" : "access-denied",
    );
  const access: unknown = await response.json();
  if (
    !access ||
    typeof access !== "object" ||
    !("role" in access) ||
    (access.role !== "admin" && access.role !== "viewer")
  )
    throw new SafeAuthError("access-denied");
  const role = access.role;
  return Object.freeze({
    session: Object.freeze({
      uid: user.uid,
      role,
      provider: provider as "google.com" | "password",
      secondFactor: "totp",
    }),
    accessToken: Object.freeze({ token: token.token, expiresAtMs }),
  });
}

const firebaseApp =
  getApps().find((app) => app.name === "[DEFAULT]") ??
  initializeApp(CENTRAL_FIREBASE_CONFIG);

export class FirebaseAuthGateway implements AuthGateway {
  readonly #auth: Auth;
  readonly #persistenceReady: Promise<void>;
  #resolver: MultiFactorResolver | null = null;
  #enrollmentSecret: TotpSecret | null = null;
  #enrollmentUser: User | null = null;
  #verifiedSessionUser: User | null = null;
  #accessToken: CachedFirebaseAccessToken | null = null;

  constructor(auth: Auth = getAuth(firebaseApp)) {
    this.#auth = auth;
    // Account-security emails are Arabic regardless of the interface locale.
    auth.languageCode = "ar";
    this.#persistenceReady = setPersistence(
      auth,
      selectFirebaseAuthPersistence({
        projectId: CENTRAL_FIREBASE_CONFIG.projectId ?? "",
        origin: window.location.origin,
      }),
    );
  }

  #rememberVerifiedSession(user: User, verified: VerifiedFirebaseSession) {
    this.#verifiedSessionUser = user;
    this.#accessToken = verified.accessToken;
    return verified.session;
  }

  #clearVerifiedSession() {
    this.#verifiedSessionUser = null;
    this.#accessToken = null;
  }

  async restore(): Promise<RestoredAuthState> {
    await this.#persistenceReady;
    await this.#auth.authStateReady();
    const user = this.#auth.currentUser;
    if (!user) {
      this.#clearVerifiedSession();
      return Object.freeze({ kind: "signed-out" });
    }
    try {
      const verified = await secureSession(user);
      return Object.freeze({
        kind: "authenticated",
        session: this.#rememberVerifiedSession(user, verified),
      });
    } catch {
      this.#clearVerifiedSession();
      await firebaseSignOut(this.#auth);
      return Object.freeze({ kind: "signed-out" });
    }
  }

  async signInWithPassword(
    email: string,
    password: string,
  ): Promise<PrimarySignInResult> {
    await this.#persistenceReady;
    this.#clearVerifiedSession();
    try {
      return await this.#completePrimary(
        await signInWithEmailAndPassword(this.#auth, email.trim(), password),
      );
    } catch (error) {
      return this.#handlePrimaryFailure(error);
    }
  }

  async signInWithGoogle(): Promise<PrimarySignInResult> {
    await this.#persistenceReady;
    this.#clearVerifiedSession();
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({ prompt: "select_account" });
    try {
      return await this.#completePrimary(
        await signInWithPopup(this.#auth, provider),
      );
    } catch (error) {
      return this.#handlePrimaryFailure(error);
    }
  }

  async #completePrimary(
    credential: UserCredential,
  ): Promise<PrimarySignInResult> {
    const user = credential.user;
    if (!user.emailVerified) {
      await firebaseSignOut(this.#auth);
      throw new SafeAuthError("access-denied");
    }
    if (multiFactor(user).enrolledFactors.length === 0) {
      const session = await multiFactor(user).getSession();
      const secret = await TotpMultiFactorGenerator.generateSecret(session);
      const accountLabel = user.email ?? user.uid;
      this.#enrollmentSecret = secret;
      this.#enrollmentUser = user;
      return Object.freeze({
        kind: "mfa-enrollment",
        accountLabel,
        secretKey: secret.secretKey,
        setupUrl: secret.generateQrCodeUrl(accountLabel, TOTP_ISSUER),
      });
    }
    const verified = await secureSession(user);
    return Object.freeze({
      kind: "authenticated",
      session: this.#rememberVerifiedSession(user, verified),
    });
  }

  #handlePrimaryFailure(error: unknown): PrimarySignInResult {
    if (
      (error as FirebaseAuthFailure | null)?.code ===
      "auth/multi-factor-auth-required"
    ) {
      try {
        const resolver = getMultiFactorResolver(
          this.#auth,
          error as MultiFactorError,
        );
        const totpHint = resolver.hints.find(
          (hint) => hint.factorId === TotpMultiFactorGenerator.FACTOR_ID,
        );
        if (!totpHint) throw new SafeAuthError("access-denied");
        this.#resolver = resolver;
        return Object.freeze({ kind: "mfa-challenge" });
      } catch (resolverError) {
        if (resolverError instanceof SafeAuthError) throw resolverError;
        throw toSafeFirebaseAuthError(resolverError);
      }
    }
    if (error instanceof SafeAuthError) throw error;
    throw toSafeFirebaseAuthError(error);
  }

  async completeMfaChallenge(code: string): Promise<AuthenticatedSession> {
    const resolver = this.#resolver;
    const totpHint = resolver?.hints.find(
      (hint) => hint.factorId === TotpMultiFactorGenerator.FACTOR_ID,
    );
    const normalizedCode = validMfaCode(code);
    if (!resolver || !totpHint || !normalizedCode) {
      throw new SafeAuthError("invalid-mfa-code");
    }
    try {
      const assertion = TotpMultiFactorGenerator.assertionForSignIn(
        totpHint.uid,
        normalizedCode,
      );
      const credential = await resolver.resolveSignIn(assertion);
      this.#resolver = null;
      const verified = await secureSession(credential.user);
      return this.#rememberVerifiedSession(credential.user, verified);
    } catch (error) {
      if (error instanceof SafeAuthError) throw error;
      throw toSafeFirebaseAuthError(error);
    }
  }

  async completeMfaEnrollment(code: string): Promise<void> {
    const user = this.#enrollmentUser;
    const secret = this.#enrollmentSecret;
    const normalizedCode = validMfaCode(code);
    if (!user || !secret || !normalizedCode) {
      throw new SafeAuthError("invalid-mfa-code");
    }
    try {
      const assertion = TotpMultiFactorGenerator.assertionForEnrollment(
        secret,
        normalizedCode,
      );
      await multiFactor(user).enroll(assertion, "Authenticator app");
      this.#enrollmentSecret = null;
      this.#enrollmentUser = null;
      await firebaseSignOut(this.#auth);
    } catch (error) {
      throw toSafeFirebaseAuthError(error);
    }
  }

  async getAccessToken(forceRefresh = false): Promise<string> {
    const cached = forceRefresh
      ? null
      : usableCachedAccessToken(this.#accessToken);
    if (cached) return cached;

    await this.#persistenceReady;
    await this.#auth.authStateReady();
    const user = authenticatedTokenUser(
      this.#auth.currentUser,
      this.#verifiedSessionUser,
    );
    if (!user) throw new SafeAuthError("session-expired");
    try {
      const verified = await secureSession(user, forceRefresh);
      this.#rememberVerifiedSession(user, verified);
      return verified.accessToken.token;
    } catch (error) {
      const safeError =
        error instanceof SafeAuthError ? error : toSafeFirebaseAuthError(error);
      if (
        safeError.code !== "network-unavailable" &&
        safeError.code !== "unexpected-error"
      ) {
        this.#clearVerifiedSession();
      }
      throw safeError;
    }
  }

  async cancelMfa(): Promise<void> {
    this.#resolver = null;
    this.#enrollmentSecret = null;
    this.#enrollmentUser = null;
    this.#clearVerifiedSession();
    await firebaseSignOut(this.#auth);
  }

  async signOut(): Promise<void> {
    this.#clearVerifiedSession();
    await firebaseSignOut(this.#auth);
  }
}
